import { ParentTypes as Parent_Type_Enum } from '@risksmart-app/domain/src/types/consts/parent-type';
import type { RelationFile } from '@risksmart-app/shared/forms/shared-schemas/fileSchema';
import { ownerContributorAndViewerIds } from 'src/components/form';

import { useInsertChildAction } from '@/hooks/mutations/action/useInsertChildAction';
import { useFileUpdate } from '@/hooks/mutations/file/useFileUpdate';
import {
  type CreatedEntityDestination,
  CreatedEntityFileSaveError,
  createWithFiles,
} from '@/utils/createWithFiles';
import { actionDetailsUrl } from '@/utils/urls';

import type { ActionFormFieldData } from './update/forms/actionsSchema';

interface UseSaveActionArgs {
  /**
   * Undefined on the register page, where a new action has no parent — the
   * procedure takes `ParentId` nullable-optional and coalesces it to null.
   */
  parentId?: string;
  /** The caller's register query refetch. */
  refetch: () => void | Promise<unknown>;
  /**
   * Anything the caller must do with the new action once its files are attached
   * and before the refetch renders the result. The obligation-change detail page
   * links the action to its obligation and closes its modal here.
   */
  onCreated?: (actionId: string) => void | Promise<void>;
}

/**
 * Creates an action and attaches its files, shared by the actions register, the
 * actions tab and the obligation-change detail page.
 *
 * The order matters. `useInsertChildAction` invalidates on insert success —
 * before `updateFiles` resolves — and the register reads are `no-cache`, so
 * neither that invalidation nor an Apollo eviction can refresh them. The
 * caller's `refetch` after the files land is what puts the new action on the
 * table with its files (COI-577).
 */
export const useSaveAction = ({
  parentId,
  refetch,
  onCreated,
}: UseSaveActionArgs) => {
  const { insertChildAction, loading } = useInsertChildAction();
  const { updateFiles } = useFileUpdate();

  const saveAction = async (values: ActionFormFieldData) => {
    const { files } = values;
    let createdEntity: CreatedEntityDestination | undefined;

    try {
      const created = await createWithFiles({
        create: () =>
          insertChildAction({
            ...values,
            ParentId: parentId,
            CustomAttributeData: values.CustomAttributeData || undefined,
            DepartmentTypeIds:
              values.departments?.map((d) => d.DepartmentTypeId) || [],
            TagTypeIds: values.tags?.map((t) => t.TagTypeId) || [],
            ...ownerContributorAndViewerIds(values),
          }),
        getCreatedEntity: (result) => {
          const actionId = result?.insertChildAction?.Id;
          if (!actionId) {
            throw new Error('Missing actionId');
          }

          createdEntity = {
            id: actionId,
            destination: actionDetailsUrl(actionId),
          };

          return createdEntity;
        },
        saveFiles: ({ id }) =>
          updateFiles({
            parentType: Parent_Type_Enum.Action,
            parentId: id,
            originalFiles: values.files?.filter(
              (file) => !(file instanceof File)
            ) as RelationFile[],
            selectedFiles: files ?? [],
          }),
      });

      try {
        await onCreated?.(created.createdEntity.id);
      } catch (cause) {
        throw new CreatedEntityFileSaveError({
          ...created.createdEntity,
          cause,
          failureStage: 'linking',
        });
      }

      return {
        savedIds: [created.createdEntity.id],
        createdEntity: created.createdEntity,
      };
    } finally {
      if (createdEntity) {
        void Promise.resolve(refetch()).catch(() => undefined);
      }
    }
  };

  return { saveAction, loading };
};
